<template>
  <v-container>
    <v-overlay :absolute="true" :opacity="0.2" :value="submiting" :z-index="5"
      >Submitting...</v-overlay
    >

    <v-snackbar
      v-model="message.visible"
      :color="message.type"
      :timeout="message.duration"
      top
    >
      {{ message.body }}
      <v-btn dark text @click="message.visible = false">Close</v-btn>
    </v-snackbar>

    <p v-if="activeImport">
      A current import job is in progress. Click
      <v-btn
        class="px-0"
        text
        color="secondary"
        @click="$router.push('/import/activities')"
        >here
      </v-btn>
      to check status.
    </p>

    <p :class="{ 'disabled-text': activeImport }">
      Import a copy from your Facebook or Instagram account into Uhive, make sure it is Zip file and in (JSON) format:
    </p>

    <v-radio-group :disabled="activeImport" v-model="importData.source" row>
      <v-radio
        v-for="(display, value) in sources"
        :key="value"
        color="secondary"
        :label="display"
        :value="value"
      ></v-radio>
    </v-radio-group>

    <p class="my-6" :class="{ 'disabled-text': activeImport }">
      Your full profile will be imported into Uhive. This includes your posts,
      and all of your media.
    </p>

    <span class="text-sm">
      <vue2Dropzone
        ref="dropzone"
        id="dropzone"
        :options="dropzoneOptions"
        v-on:vdropzone-error="uploadError"
        v-on:vdropzone-success="uploadSuccess"
        v-on:vdropzone-sending="sending"
        v-on:vdropzone-canceled="abortUpload"
        :class="dropzoneClass"
      ></vue2Dropzone>
      Max file size 10 GB.
    </span>

    <v-row justify="center">
      <v-dialog v-model="spaceDialog" scrollable max-width="300px">
        <template v-slot:activator="{ on }">
          <v-btn
            outlined
            @click="fetchSpaces()"
            :disabled="!step2Completed"
            color="secondary"
            v-on="on"
            >Next
          </v-btn>
        </template>
        <v-card>
          <v-card-title>Select a space:</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="height: 300px">
            <div v-show="loadingSpaces" class="text-center mt-12">
              <v-progress-circular
                size="30"
                indeterminate
              ></v-progress-circular>
            </div>
            <div v-if="!loadingSpaces">
              <v-radio-group
                v-if="!!spaces.length"
                v-model="importData.spaceId"
                column
              >
                <v-radio
                  v-for="(space, index) in spaces"
                  :key="index"
                  :label="`${space.name} (${space.postCount} Posts) - ${space.interest.name}`"
                  :value="space.id"
                  color="secondary"
                ></v-radio>
              </v-radio-group>
              <p class="text-center mt-12" v-else>
                No spaces available, please create a space first to import your
                data.
              </p>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn color="blue" text @click="spaceDialog = false">Close</v-btn>
            <v-btn
              :disabled="!step3Completed"
              color="blue"
              @click="handleImport() && (spaceDialog = false)"
              text
              >Import
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { isURL } from "../../../helpers/validators";
import MessagingMixin from "../../../mixins/messaging";
import { startUpload, abortUpload } from "../services/api";
import { importTypes } from "../store";
import { get_auth_cookie } from "../../../helpers/auth";

export default {
  mixins: [MessagingMixin],
  components: {
    vue2Dropzone,
  },
  data() {
    return {
      sources: importTypes,
      importData: {
        uploadID: null,
        s3ProfileLink: null,
        spaceId: null,
        source: null,
      },
      submiting: false,
      loadingSpaces: false,
      spaceDialog: false,
      dropzoneOptions: {
        url: process.env.VUE_APP_BASE_API_URL + "social_imports/upload",
        headers: {
          BUILDNO: process.env.VUE_APP_BUILD_NO,
          Authorization: `Bearer ` + get_auth_cookie(),
        },
        maxFiles: 1,
        parallelUploads: 1,
        addRemoveLinks: true,
        maxFilesize: 10000,
        forceChunking: true,
        retryChunks: true,
        chunking: true,
        chunkSize: 6000000,
        accept: this.initiateUpload,
      },
    };
  },
  computed: {
    step1Completed() {
      return !!this.importData.source;
    },
    step2Completed() {
      return isURL(this.importData.s3ProfileLink);
    },
    step3Completed() {
      return !!this.importData.spaceId;
    },
    dropzoneClass() {
      return {
        "disabled-clicking": this.activeImport || !this.step1Completed,
      };
    },
    spaces() {
      return this.$store.getters["user/spaces"];
    },
    activeImport() {
      let [active] = this.$store.getters["imports/allImports"].filter(
        (item) => item.status == "Processing"
      );
      return !!active;
    },
  },
  methods: {
    fetchSpaces() {
      if (this.spaces.length > 0) return;
      this.loadingSpaces = true;
      this.$store
        .dispatch("user/fetchUserSpaces", {
          only_active: true,
          page_size: 50,
        })
        .finally(() => {
          this.loadingSpaces = false;
        });
    },
    handleImport() {
      this.submiting = true;

      let profileLink = this.importData.s3ProfileLink;
      let spaceId = this.importData.spaceId;
      let importType = this.importData.source;

      return this.$store
        .dispatch("imports/importProfile", { profileLink, spaceId, importType })
        .then(() => {
          this.submiting = false;
          setTimeout(() => this.$router.push("/import/activities"), 2000);
          this.setMessage("Profile submitted successfully", "success");
        })
        .catch((error) => {
          this.submiting = false;
          this.setMessage(error.message, "error");
        });
    },
    initiateUpload(file, done) {
      let { total, totalChunkCount } = file.upload;
      startUpload(total, totalChunkCount)
        .then((data) => {
          this.importData.uploadID = data.upload_id;
        })
        .then(done);
    },
    abortUpload() {
      abortUpload(this.importData.uploadID);
    },
    sending(file, xhr, formData) {
      formData.append("upload_id", this.importData.uploadID);
    },
    uploadError(file, message, xhr) {
      if (xhr) {
        this.setMessage("Error occured while uploading your profile.", "error");
      }
    },
    uploadSuccess(file) {
      let response = JSON.parse(file.xhr.response);
      this.importData.s3ProfileLink = response.location;
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  text-align: center;
}
.disabled-text {
  color: lightgray;
}
.disabled-clicking {
  pointer-events: none;
}
.text-sm {
  font-size: 12px;
}
</style>